import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { AlertCircle } from "lucide-react";
import { apiClient } from "@/shared/services/api";

interface AddonConfirmationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  addonType?: 'extra_user' | 'storage_90_days' | 'storage_1_year';
  addonPrice?: number;
  isTrialUser: boolean;
  daysLeftInTrial?: number;
  loading?: boolean;
  currentUsers?: number;
  maxUsers?: number;
}

interface ReferralDiscount {
  hasDiscount: boolean;
  discountAmount: number;
  discountedBasePrice: number;
  couponId?: string;
  duration?: string;
  durationInMonths?: number;
  referralCode?: string;
}

const ADDON_CONFIG = {
  extra_user: {
    name: 'Additional User',
    description: 'This will add an additional user seat to your plan and update your monthly billing accordingly.',
  },
  storage_90_days: {
    name: '90 Day Storage',
    description: 'Extend your video storage retention to 90 days from the day they were filmed.',
  },
  storage_1_year: {
    name: '1 Year Storage',
    description: 'Extend your video storage retention to 1 year (365 days) from the day they were filmed.',
  },
};

export function AddonConfirmationDialog({
  open,
  onOpenChange,
  onConfirm,
  addonType,
  addonPrice,
  isTrialUser,
  daysLeftInTrial,
  loading = false,
  currentUsers = 0,
  maxUsers = 2,
}: AddonConfirmationDialogProps) {
  const [referralDiscount, setReferralDiscount] = useState<ReferralDiscount | null>(null);
  const [loadingDiscount, setLoadingDiscount] = useState(true);

  // Fetch referral discount when dialog opens
  useEffect(() => {
    if (open && isTrialUser) {
      fetchReferralDiscount();
    }
  }, [open, isTrialUser]);

  const fetchReferralDiscount = async () => {
    try {
      setLoadingDiscount(true);
      const response = await apiClient.get<{ ok: boolean; hasDiscount?: boolean; discountAmount?: number; discountedBasePrice?: number; couponId?: string; duration?: string; durationInMonths?: number; referralCode?: string }>('/billing/referral-discount');

      if (response.ok && response.hasDiscount) {
        setReferralDiscount({
          hasDiscount: true,
          discountAmount: response.discountAmount || 0,
          discountedBasePrice: response.discountedBasePrice || 99,
          couponId: response.couponId,
          duration: response.duration,
          durationInMonths: response.durationInMonths,
          referralCode: response.referralCode,
        });
      } else {
        setReferralDiscount({
          hasDiscount: false,
          discountAmount: 0,
          discountedBasePrice: 99,
        });
      }
    } catch (error) {
      console.error('[AddonConfirmationDialog] Failed to fetch referral discount:', error);
      setReferralDiscount({
        hasDiscount: false,
        discountAmount: 0,
        discountedBasePrice: 99,
      });
    } finally {
      setLoadingDiscount(false);
    }
  };

  if (!addonType || addonPrice === undefined) return null;

  const addon = ADDON_CONFIG[addonType];
  const basePlanPrice = referralDiscount?.discountedBasePrice ?? 99;

  // Calculate current extra users (base plan includes 2 users, so subtract 2)
  const currentExtraUsers = Math.max(0, maxUsers - 2);

  // Calculate what the new total will be
  let newTotalPrice = basePlanPrice;

  if (addonType === 'extra_user') {
    // Adding another user: base + (current extra users + 1) * $49.99
    const newExtraUsers = currentExtraUsers + 1;
    newTotalPrice = basePlanPrice + (newExtraUsers * 49.99);
  } else {
    // Storage addon: base + current extra users * $49.99 + storage addon price
    newTotalPrice = basePlanPrice + (currentExtraUsers * 49.99) + addonPrice;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isTrialUser ? 'Subscribe with Add-on' : 'Confirm Add-on'}
          </DialogTitle>
          <DialogDescription asChild>
            <div className="space-y-4 pt-4">
              {isTrialUser ? (
                // Trial user warning
                <>
                  <div className="flex items-start gap-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-yellow-800">
                      <p className="font-medium">Your trial will end</p>
                      <p className="mt-1">
                        You're currently on a trial with <strong>{daysLeftInTrial || 0} days</strong> remaining.
                      </p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <p className="text-sm text-foreground">
                      By continuing, you'll subscribe to:
                    </p>
                    <div className="space-y-2 pl-4">
                      <div className="flex items-start gap-2">
                        <span className="text-green-600 mt-0.5">✓</span>
                        <div className="text-sm">
                          <span className="font-medium">Base Plan</span>
                          {referralDiscount?.hasDiscount ? (
                            <>
                              <span className="text-muted-foreground line-through ml-1">${99}/month</span>
                              <span className="text-green-600 font-semibold ml-1">${basePlanPrice.toFixed(2)}/month</span>
                              <span className="text-xs text-green-600 ml-1">(Referral discount applied)</span>
                            </>
                          ) : (
                            <span className="text-muted-foreground"> (${basePlanPrice}/month)</span>
                          )}
                        </div>
                      </div>
                      {currentExtraUsers > 0 && (
                        <div className="flex items-start gap-2">
                          <span className="text-green-600 mt-0.5">✓</span>
                          <div className="text-sm">
                            <span className="font-medium">{currentExtraUsers} Extra User{currentExtraUsers > 1 ? 's' : ''}</span>
                            <span className="text-muted-foreground"> (${(currentExtraUsers * 49.99).toFixed(2)}/month)</span>
                          </div>
                        </div>
                      )}
                      <div className="flex items-start gap-2">
                        <span className="text-green-600 mt-0.5">✓</span>
                        <div className="text-sm">
                          <span className="font-medium">{addon.name}</span>
                          <span className="text-muted-foreground"> (${addonPrice.toFixed(2)}/month)</span>
                        </div>
                      </div>
                    </div>

                    <div className="pt-2 border-t">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold">New Total:</span>
                        <span className="text-lg font-bold">${newTotalPrice.toFixed(2)}/month</span>
                      </div>
                    </div>
                  </div>

                  <p className="text-sm text-muted-foreground">
                    Ready to subscribe?
                  </p>
                </>
              ) : (
                // Paid user simple confirmation
                <>
                  <p className="text-sm text-foreground">
                    {addon.description}
                  </p>

                  <div className="p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Price:</span>
                      <span className="text-base font-semibold">${addonPrice.toFixed(2)} per month</span>
                    </div>
                  </div>

                  <p className="text-sm text-muted-foreground">
                    This will be added to your next billing cycle.
                  </p>

                  <p className="text-sm text-foreground">
                    Do you want to continue?
                  </p>
                </>
              )}
            </div>
          </DialogDescription>
        </DialogHeader>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? 'Processing...' : isTrialUser ? 'Continue to Checkout' : 'Confirm'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
